/**
 * Distribución de teclado US (ANSI), la que emula un lector Zebra con la
 * configuración de fábrica ("North American"). El lector no manda caracteres:
 * manda teclas físicas, y el sistema operativo las traduce con la distribución
 * del host. Si el host está en español, la tecla física del `-` produce `'`,
 * la del `/` produce `-`, y las de `[` y `'` son acentos muertos que se
 * pierden o se combinan con la letra siguiente.
 *
 * `caracterUs` devuelve el carácter que el lector QUISO transmitir a partir de
 * la tecla física (`KeyboardEvent.code`) y Shift, independientemente del
 * layout del host. Cubre los dígitos y la puntuación ASCII del juego de
 * caracteres de GS1; para las letras y cualquier otra tecla devuelve null.
 *
 * Supuesto documentado: los lectores quedan en la distribución de fábrica. Un
 * lector reconfigurado a "Spanish" sobre un host en español ya no necesita
 * corrección, y esta tabla lo corrompería. Si eso pasa, lo delata la traza de
 * teclas del log de lecturas (`k` correcto con `c` que no coincide).
 */

const DIGITOS_CON_SHIFT = ')!@#$%^&*(';

const SIN_SHIFT: Readonly<Record<string, string>> = {
  Backquote: '`',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: String.fromCharCode(92),
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  Space: ' ',
};

const CON_SHIFT: Readonly<Record<string, string>> = {
  Backquote: '~',
  Minus: '_',
  Equal: '+',
  BracketLeft: '{',
  BracketRight: '}',
  Backslash: '|',
  Semicolon: ':',
  Quote: '"',
  Comma: '<',
  Period: '>',
  Slash: '?',
  Space: ' ',
};

export function caracterUs(code: string, shift: boolean): string | null {
  const digito = /^Digit(\d)$/.exec(code)?.[1];
  if (digito) return shift ? (DIGITOS_CON_SHIFT[Number(digito)] ?? null) : digito;
  // Letras: a propósito null. Son iguales en US y en español, y su caja
  // depende de CapsLock del host o de la configuración del lector, que no es
  // un problema de distribución. Se respeta lo que produjo el host.
  if (/^Key[A-Z]$/.test(code)) return null;
  return (shift ? CON_SHIFT : SIN_SHIFT)[code] ?? null;
}
